import { type FormEvent, useEffect, useState } from 'react';
import { BarChart3, Building2, Eye, FileText, LogOut, ShieldCheck } from 'lucide-react';
import { CASE_STATUSES, type CaseStatus } from '../../shared/case-schema.js';
import type { AdminInvitation, AdminReportRow, AdminVenue } from '../admin-types.js';
import { adminApi } from '../api.js';
import { AdminReportDrawer } from '../components/AdminReportDrawer.js';
import { AdminVenuesPanel } from '../components/AdminVenuesPanel.js';
import { AppShell } from '../components/AppShell.js';
import { StatusBadge } from '../components/StatusBadge.js';
import { Button, Field, Input, Loading, Notice } from '../components/ui.js';

interface Metrics {
  starts: number;
  completed: number;
  completionRate: number;
  medianTotalDurationMs: number | null;
  medianVoiceDurationMs: number | null;
  textFallbackRate: number;
  mandatoryFieldCorrectionRate: number;
}

interface Audit {
  id: string;
  action: string;
  targetType: string;
  targetId: string;
  createdAt: string;
  actorEmailHash: string;
}

export function AdminPage() {
  const [authState, setAuthState] = useState<'checking' | 'signedOut' | 'signedIn'>('checking');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => {
    void adminApi<{ username: string }>('/api/admin/session')
      .then(() => setAuthState('signedIn'))
      .catch(() => setAuthState('signedOut'));
  }, []);
  const signIn = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setAuthError('');
    setSubmitting(true);
    try {
      await adminApi('/api/admin/login', {
        method: 'POST',
        body: JSON.stringify({ username, password }),
      });
      setPassword('');
      setAuthState('signedIn');
    } catch (error) {
      setPassword('');
      setAuthError(error instanceof Error ? error.message : 'Sign-in failed');
    } finally {
      setSubmitting(false);
    }
  };
  const logout = async () => {
    await adminApi('/api/admin/logout', { method: 'POST' });
    setAuthState('signedOut');
    setAuthError('');
  };
  if (authState === 'checking') {
    return (
      <AppShell language="en">
        <Loading label="Loading admin workspace..." />
      </AppShell>
    );
  }
  if (authState === 'signedOut') {
    return (
      <AppShell language="en">
        <section className="admin-login">
          <ShieldCheck aria-hidden />
          <h1>Refindi administration</h1>
          <p>Sign in to manage reports, return cases, venues, and invitations.</p>
          {authError ? <Notice tone="danger">{authError}</Notice> : null}
          <form className="admin-login-form" onSubmit={(event) => void signIn(event)}>
            <Field label="Username">
              <Input
                name="username"
                autoComplete="username"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                autoFocus
                required
              />
            </Field>
            <Field label="Password">
              <Input
                name="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
              />
            </Field>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Signing in...' : 'Sign in'}
            </Button>
          </form>
        </section>
      </AppShell>
    );
  }
  return <AdminDashboard logout={logout} />;
}

function AdminDashboard({ logout }: { logout: () => Promise<void> }) {
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [rows, setRows] = useState<AdminReportRow[]>([]);
  const [venues, setVenues] = useState<AdminVenue[]>([]);
  const [invitations, setInvitations] = useState<AdminInvitation[]>([]);
  const [audit, setAudit] = useState<Audit[]>([]);
  const [selected, setSelected] = useState<AdminReportRow | null>(null);
  const [statusFilter, setStatusFilter] = useState<CaseStatus | ''>('');
  const [tab, setTab] = useState<'reports' | 'venues' | 'audit'>('reports');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const load = async (showLoading = false) => {
    if (showLoading) setLoading(true);
    setError('');
    try {
      const query = statusFilter ? `?status=${statusFilter}` : '';
      const [metricsValue, reportValue, venueValue, auditValue] = await Promise.all([
        adminApi<Metrics>('/api/admin/metrics'),
        adminApi<{ rows: AdminReportRow[] }>(`/api/admin/reports${query}`),
        adminApi<{ venues: AdminVenue[]; invitations: AdminInvitation[] }>('/api/admin/venues'),
        adminApi<{ audit: Audit[] }>('/api/admin/audit'),
      ]);
      setMetrics(metricsValue);
      setRows(reportValue.rows);
      setVenues(venueValue.venues);
      setInvitations(venueValue.invitations);
      setAudit(auditValue.audit);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to load data');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load(true);
  }, [statusFilter]);
  return (
    <AppShell language="en">
      <div className="admin-header">
        <div>
          <h1>Lost-item reports</h1>
          <p>Review reports and manage each item through return or closure.</p>
        </div>
        <Button variant="ghost" onClick={() => void logout()}>
          <LogOut size={17} /> Sign out
        </Button>
      </div>
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {metrics ? <MetricGrid metrics={metrics} /> : null}
      <nav className="admin-tabs" aria-label="Administration sections">
        <button className={tab === 'reports' ? 'active' : ''} onClick={() => setTab('reports')}>
          <FileText size={17} /> Reports
        </button>
        <button className={tab === 'venues' ? 'active' : ''} onClick={() => setTab('venues')}>
          <Building2 size={17} /> Venues and invitations
        </button>
        <button className={tab === 'audit' ? 'active' : ''} onClick={() => setTab('audit')}>
          <ShieldCheck size={17} /> Audit
        </button>
      </nav>
      {loading ? (
        <Loading label="Loading data..." />
      ) : tab === 'reports' ? (
        <>
          <label className="admin-status-filter">
            <span>Case status</span>
            <select
              className="input"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value as CaseStatus | '')}
            >
              <option value="">All statuses</option>
              {CASE_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {status.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </label>
          <ReportsTable rows={rows} onSelect={setSelected} />
        </>
      ) : tab === 'venues' ? (
        <AdminVenuesPanel venues={venues} invitations={invitations} reload={() => load()} />
      ) : (
        <AuditTable audit={audit} />
      )}
      {selected ? (
        <AdminReportDrawer
          row={selected}
          close={() => setSelected(null)}
          reloadReports={() => load()}
        />
      ) : null}
    </AppShell>
  );
}

function MetricGrid({ metrics }: { metrics: Metrics }) {
  const items = [
    ['Started', String(metrics.starts)],
    ['Completed', String(metrics.completed)],
    ['Completion rate', percent(metrics.completionRate)],
    ['Median total time', duration(metrics.medianTotalDurationMs)],
    ['Median voice time', duration(metrics.medianVoiceDurationMs)],
    ['Text fallback', percent(metrics.textFallbackRate)],
    ['Field corrections', percent(metrics.mandatoryFieldCorrectionRate)],
  ];
  return (
    <section className="metric-grid" aria-label="Service metrics">
      {items.map(([label, value]) => (
        <article key={label}>
          <BarChart3 size={18} />
          <span>{label}</span>
          <strong>{value}</strong>
        </article>
      ))}
    </section>
  );
}

function ReportsTable({
  rows,
  onSelect,
}: {
  rows: AdminReportRow[];
  onSelect: (row: AdminReportRow) => void;
}) {
  if (!rows.length) {
    return (
      <div className="empty-state">
        <FileText />
        <h2>No reports found</h2>
        <p>New submissions and matching case statuses will appear here.</p>
      </div>
    );
  }
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Ref.</th>
            <th>Date</th>
            <th>Venue</th>
            <th>Item</th>
            <th>Status</th>
            <th>
              <span className="sr-only">Action</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.report.reportId}>
              <td>
                <strong>{row.report.reportId.slice(0, 8).toUpperCase()}</strong>
              </td>
              <td>{new Date(row.report.createdAt).toLocaleString('en-GB')}</td>
              <td>{row.report.venueContext.venueName}</td>
              <td>{row.report.item.description}</td>
              <td>
                <StatusBadge status={row.caseStatus} />
              </td>
              <td>
                <button
                  className="icon-button"
                  aria-label="Open report"
                  onClick={() => onSelect(row)}
                >
                  <Eye size={17} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AuditTable({ audit }: { audit: Audit[] }) {
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Date</th>
            <th>Action</th>
            <th>Type</th>
            <th>Target</th>
            <th>Administrator</th>
          </tr>
        </thead>
        <tbody>
          {audit.map((entry) => (
            <tr key={entry.id}>
              <td>{new Date(entry.createdAt).toLocaleString('en-GB')}</td>
              <td>{entry.action}</td>
              <td>{entry.targetType}</td>
              <td>{entry.targetId.slice(0, 16)}</td>
              <td>{entry.actorEmailHash.slice(0, 10)}...</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function percent(value: number) {
  return `${Math.round(value * 100)}%`;
}

function duration(value: number | null) {
  if (value === null) return '–';
  const seconds = Math.round(value / 1000);
  return seconds >= 60 ? `${Math.floor(seconds / 60)}m ${seconds % 60}s` : `${seconds}s`;
}
